import React, { useState, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import TaxManagement from './TaxManagement';
import { getCurrentUser } from '.././Authentication/auth';
import PropertySettings from './Pages/PropertySettings';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { IoLocation } from 'react-icons/io5';

// Import the new Policies component
import Policies from './Policies';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function PropertyPage() {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'settings', 'taxes', or 'policies'

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyD94k6ko-gr9rDuePv-LKRzoL_44Xh36UE',
    libraries: ['places'],
  });

  useEffect(() => {
    fetchProperty();
    getUserLocation();
  }, []);

  const fetchProperty = async () => {
    try {
      const currentUser = await getCurrentUser();
      console.log(currentUser);
      if (!currentUser) {
        toast.error('Failed to fetch user data. Please log in again.');
        return;
      }
      const response = await fetch(`${BQ_BASE_URL}/bq/api/properties/${currentUser.property_id}`, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to fetch property');

      const data = await response.json();
      setProperty(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
        },
        (error) => console.error('Error getting location:', error)
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const handlePropertyUpdate = (updatedProperty) => {
    setProperty(updatedProperty);
  };

  if (loading)
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  if (error)
    return (
      <div className="flex justify-center items-center min-h-screen text-red-500">
        Error: {error}
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className=" mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h1 className="text-2xl font-bold text-gray-800">Property Management System</h1>
              <p className="text-sm font-semibold text-gray-500 mt-1">
                Manage your property details and operations.
              </p>
            </div>

            <div className="">
              <div className="flex items-start">
                <div>
                  {property?.logo_url ? (
                    <div className="flex justify-start">
                      <img src={property.logo_url} alt="Logo" className="h-24 w-40 object-fill" />
                    </div>
                  ) : (
                    <div className="flex items-center text-gray-600 mt-1">
                      <div className="mr-2 mt-1 text-red-500">
                        <IoLocation size={32} />
                      </div>
                      <h2 className="text-4xl  font-bold text-blue-500">
                        {property?.propertyname}
                      </h2>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Tabs - Added Policies tab */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Property Details
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Property Settings
            </button>
            <button
              onClick={() => setActiveTab('taxes')}
              className={`${
                activeTab === 'taxes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Tax & Service Fee Management
            </button>
            <button
              onClick={() => setActiveTab('policies')}
              className={`${
                activeTab === 'policies'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Discount Policies
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Property Information</h3>
            </div>
            <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Property Name</label>
                  <div className="mt-1 p-2 bg-gray-50 rounded-md border">
                    {property.propertyname}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Hotel Name</label>
                  <div className="mt-1 p-2 bg-gray-50 rounded-md border">{property.name}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Address</label>
                  <div className="mt-1 p-2 bg-gray-50 rounded-md">{property.address}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <div className="mt-1 p-2 bg-gray-50 rounded-md">{property.city}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <div className="mt-1 p-2 bg-gray-50 rounded-md">{property.location}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <div className="mt-1 p-2 bg-gray-50 rounded-md">{property.phone}</div>
                  </div>
                  <div className="w-full max-w-md">
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <div className="mt-1 p-2 bg-gray-50 rounded-md break-words text-sm sm:text-base">
                      {property.email}
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Location Map</label>
                  <div className="mt-1 h-96 rounded-md overflow-hidden">
                    {isLoaded ? (
                      <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={userLocation || { lat: 19.076, lng: 72.8777 }}
                        zoom={15}
                      >
                        {userLocation && <Marker position={userLocation} />}
                      </GoogleMap>
                    ) : (
                      <div className="h-full flex items-center justify-center bg-gray-100">
                        Loading map...
                      </div>
                    )}
                  </div>
                  {userLocation && (
                    <div className="mt-2 text-xs text-gray-500">
                      Coordinates: {userLocation.lat.toFixed(6)}, {userLocation.lng.toFixed(6)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Property Settings</h3>
            </div>
            <div className="px-6 py-5">
              <PropertySettings property={property} onUpdate={handlePropertyUpdate} />
            </div>
          </div>
        )}

        {activeTab === 'taxes' && (
          <div>
            <div>
              <TaxManagement propertyId={property.propertyid} />
            </div>
          </div>
        )}

        {activeTab === 'policies' && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Discount Policies</h3>
              <p className="mt-1 text-sm text-gray-500">
                Manage senior citizen discounts and other membership discounts
              </p>
            </div>
            <div className="px-6 py-5">
              <Policies />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default PropertyPage;
